import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';
import ShippingEstimator from '../components/ShippingEstimator';
import SupplierPopover from '../components/SupplierPopover';
import { InventoryBadge, InventoryPanel } from '../components/InventoryDisclaimer';
import { getInventoryDisplay, nativeProductId } from '../lib/inventory';
import { resolveColorHex } from '../lib/colorHex';
import { baseProductId } from '../lib/cart';

const G = BRAND.colors.primary;
// data_source → supplier slug for the live per-size pricing endpoint.
const PRICING_SLUG = { direct_sanmar: 'sanmar', direct_ssactivewear: 'ssactivewear' };
const CATS = ['All','Bags','Drinkware','Apparel','Tech','Office'];
const EMOJIS = { Bags:'🎒',Drinkware:'🥤',Apparel:'👕',Tech:'⚡',Office:'📓' };

// Client-side pagination over the result payload the server returns (currently
// capped at limit=150 in /api/search). Server-side pagination would multiply
// the search_cache key space and force a separate AI re-rank per page; keeping
// it client-side means one search call yields a paginated browse experience.
// If users routinely need to dig past 150 results, bump the API limit param
// before introducing offset.
const PAGE_SIZE = 24;

// PSRESTful returns hex without the '#' prefix (e.g. "2D2926"). Add it for CSS.
const hexBg = (hex, alpha = '', fallback = '#F8FAFC') => {
  if (!hex) return fallback;
  const clean = hex.startsWith('#') ? hex : `#${hex}`;
  return clean + alpha;
};

// Pick best image to show on a search card (just the primary, or first if none).
const cardImageUrl = (p) => {
  const imgs = p.images || [];
  return imgs.find(i => i.type === 'primary')?.url || imgs[0]?.url || null;
};

// Build a curated, deduplicated gallery for the modal:
// 1) primary image, 2) up to one blank per partId, 3) up to 4 decorated samples.
// Caps at ~12 to keep the carousel manageable.
const buildGallery = (p) => {
  const imgs = p.images || [];
  const out = [];
  const seenUrl = new Set();
  const push = (img) => {
    if (img && img.url && !seenUrl.has(img.url)) {
      seenUrl.add(img.url);
      out.push(img);
    }
  };
  imgs.filter(i => i.type === 'primary').forEach(push);
  const seenParts = new Set();
  imgs.filter(i => i.type === 'blank').forEach(i => {
    if (!seenParts.has(i.partId)) {
      seenParts.add(i.partId);
      push(i);
    }
  });
  imgs.filter(i => i.type === 'decorated').slice(0, 4).forEach(push);
  for (const i of imgs) {
    if (out.length >= 12) break;
    push(i);
  }
  return out;
};

// Clean description: API returns array of strings, server joins with space.
// Some lines have trailing spaces creating "Recommended ." patterns — fix those.
// Also strips HTML some suppliers send in descriptions (<ul><li>…). The sync
// now stores plain text, but rows synced before that fix still carry tags.
const cleanDescription = (d) =>
  (d || '')
    .replace(/<\s*li[^>]*>/gi, ' • ').replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&sup2;/gi, '\u00b2')
    .replace(/\s+\./g, '.').replace(/\s+,/g, ',').replace(/\s{2,}/g, ' ').trim();

function fmtPrice(p) {
  const t = (p.pricing||[])[0];
  if (!t) return null;
  // SanMar/S&S store the real per-size Net directly (Product Data price is a
  // catalog number). Prefer it; otherwise derive net from list × discount code.
  if (t.net != null) return +Number(t.net).toFixed(2);
  const D={A:.5,B:.45,C:.4,D:.35,E:.3,F:.25,G:.2,P:.5,Q:.45,R:.4,S:.35,T:.3,U:.25,V:.2,X:0};
  return +(( t.list||t.l ) * (1-(D[t.code||t.c]||0))).toFixed(2);
}

// ─── Product modal ────────────────────────────────────────────────────────────
function ProductModal({ product: initialProduct, onClose, onAdd, getToken, onProductRefreshed }) {
  // Local product state lets InventoryPanel refreshes update the modal in place
  // without re-fetching the whole product. Parent passes the seed; we own
  // updates while the modal is open.
  const [p, setP] = useState(initialProduct);
  const [margin, setMargin] = useState(40);
  // Live per-size Net pricing for SanMar/S&S apparel (fetched on open).
  const [sizePricing, setSizePricing] = useState(null);
  const [sizePricingLoading, setSizePricingLoading] = useState(false);
  useEffect(() => {
    const slug = PRICING_SLUG[p.data_source];
    if (!slug) { setSizePricing(null); return; }
    let cancelled = false;
    (async () => {
      setSizePricingLoading(true);
      try {
        const token = getToken ? await getToken() : null;
        const data = await api.withToken(token).getSupplierPricing(slug, nativeProductId(p));
        if (!cancelled) {
          setSizePricing(data?.ok && data.bySize && Object.keys(data.bySize).length ? data : null);
        }
      } catch {
        if (!cancelled) setSizePricing(null);
      } finally {
        if (!cancelled) setSizePricingLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [p.id, p.data_source]);
  const hasSizePricing = !!(sizePricing && sizePricing.bySize && Object.keys(sizePricing.bySize).length);
  const [qty,    setQty]    = useState((p.pricing||[])[0]?.qty || p.min_qty || 1);
  const [imgIdx, setImgIdx] = useState(0);
  const [imgError, setImgError] = useState(false);

  const D={A:.5,B:.45,C:.4,D:.35,E:.3,F:.25,G:.2,P:.5,Q:.45,R:.4,S:.35,T:.3,U:.25,V:.2,X:0};
  const tier = (p.pricing||[]).reduce((a,t) => qty>=(t.qty||t.q)?t:a, (p.pricing||[])[0]||{});
  const net  = +(((tier.list||tier.l||0))*(1-(D[tier.code||tier.c]||0))).toFixed(2);
  const sellPrice = +(net/(1-margin/100)).toFixed(2);
  const colors = p.colors||[];
  const gallery = useMemo(() => buildGallery(p), [p]);
  const currentImg = gallery[imgIdx];
  const description = useMemo(() => cleanDescription(p.description), [p.description]);

  const goPrev = (e) => { e.stopPropagation(); setImgError(false); setImgIdx(i => (i - 1 + gallery.length) % gallery.length); };
  const goNext = (e) => { e.stopPropagation(); setImgError(false); setImgIdx(i => (i + 1) % gallery.length); };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:100,
      display:'flex',alignItems:'flex-start',justifyContent:'center',padding:24,overflowY:'auto',backdropFilter:'blur(4px)' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:'#fff',borderRadius:16,width:'100%',maxWidth:700,overflow:'hidden',marginTop:24 }}>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'18px 24px',borderBottom:'1px solid #E2E8F0' }}>
          <div>
            <div style={{ fontSize:16,fontWeight:600,color:'#0F172A' }}>{p.name}</div>
            <div style={{ fontSize:12,color:'#94A3B8',marginTop:2 }}>
              <SupplierPopover product={p}>{p.supplier_name}</SupplierPopover>
              {p.supplier_sku && <span> · #{p.supplier_sku}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none',border:'none',cursor:'pointer',fontSize:22,color:'#94A3B8',padding:'0 4px' }}>×</button>
        </div>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,padding:24 }}>
          <div>
            {/* Image gallery — neutral bg, arrows + dots */}
            <div style={{ position:'relative',height:240,borderRadius:12,background:'#F8FAFC',
              display:'flex',alignItems:'center',justifyContent:'center',marginBottom:14,overflow:'hidden' }}>
              {currentImg && !imgError ? (
                <img src={currentImg.url} alt={p.name}
                  onError={()=>setImgError(true)}
                  style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
              ) : (
                <span style={{ fontSize:80 }}>{EMOJIS[p.category]||'📦'}</span>
              )}
              {gallery.length > 1 && (
                <>
                  <button onClick={goPrev} aria-label="Previous image"
                    style={{ position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',
                      width:32,height:32,borderRadius:'50%',border:'1px solid #E2E8F0',
                      background:'rgba(255,255,255,0.9)',cursor:'pointer',fontSize:16,color:'#475569',
                      display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit' }}>‹</button>
                  <button onClick={goNext} aria-label="Next image"
                    style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',
                      width:32,height:32,borderRadius:'50%',border:'1px solid #E2E8F0',
                      background:'rgba(255,255,255,0.9)',cursor:'pointer',fontSize:16,color:'#475569',
                      display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontFamily:'inherit' }}>›</button>
                  <div style={{ position:'absolute',bottom:8,left:0,right:0,
                    display:'flex',justifyContent:'center',gap:5 }}>
                    {gallery.map((_,i)=>(
                      <div key={i} style={{ width:6,height:6,borderRadius:'50%',
                        background:i===imgIdx?G:'#CBD5E1',transition:'background 0.15s' }}/>
                    ))}
                  </div>
                  <div style={{ position:'absolute',top:8,right:8,
                    background:'rgba(15,23,42,0.7)',color:'#fff',fontSize:10,
                    padding:'2px 8px',borderRadius:10,fontWeight:500 }}>
                    {imgIdx+1} / {gallery.length}
                  </div>
                </>
              )}
            </div>
            {colors.length>0 && (
              <div>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>
                  Colors ({colors.length})
                </div>
                <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                  {colors.map((c,i)=>(
                    <div key={i} title={c.name||c.n||''}
                      style={{ width:20,height:20,borderRadius:'50%',
                        background: hexBg(resolveColorHex(c), '', '#ccc'),
                        border:'1px solid #E2E8F0' }}/>
                  ))}
                </div>
              </div>
            )}
            <div style={{ background:'#F8FAFC',borderRadius:10,padding:14,marginTop:16 }}>
              <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>Supplier</div>
              <div style={{ fontSize:13,fontWeight:500,color:'#0F172A',marginBottom:6 }}>{p.supplier_name}</div>
              {p.supplier_phone&&<div style={{ fontSize:12,color:'#64748B',marginBottom:4 }}>📞 {p.supplier_phone}</div>}
              {p.supplier_email&&<div style={{ fontSize:12,color:'#64748B',marginBottom:4 }}>✉ {p.supplier_email}</div>}
              {p.supplier_web&&<a href={`https://${p.supplier_web}`} target="_blank" rel="noreferrer"
                style={{ fontSize:12,color:G }}>🌐 {p.supplier_web}</a>}
            </div>
          </div>
          <div>
            <div style={{ fontSize:12,color:'#64748B',lineHeight:1.6,marginBottom:14 }}>{description}</div>
            <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:14 }}>
              {p.lead_time&&<span style={{ background:`${G}18`,color:'#059669',fontSize:11,padding:'3px 9px',borderRadius:6 }}>⏱ {p.lead_time}</span>}
              {p.fob&&<span style={{ background:'#F1F5F9',color:'#475569',fontSize:11,padding:'3px 9px',borderRadius:6 }}>📍 {p.fob}</span>}
            </div>
            <InventoryPanel product={p} getToken={getToken} onRefreshed={(u)=>{ setP(u); onProductRefreshed?.(u); }}/>
            {PRICING_SLUG[p.data_source] ? (
              /* SanMar / S&S: per-size Net cost only — never the list-price qty table */
              hasSizePricing ? (
                <>
                  <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>
                    Pricing by size · your {sizePricing.priceType||'Net'} cost
                  </div>
                  <table style={{ width:'100%',borderCollapse:'collapse',marginBottom:14 }}>
                    <thead><tr>
                      {['Size','Your cost',`At ${margin}% margin`].map(h=>(
                        <th key={h} style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',padding:'5px 8px',textAlign:'left',borderBottom:'1px solid #F1F5F9' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{Object.entries(sizePricing.bySize).map(([size,cost])=>(
                      <tr key={size}>
                        <td style={{ padding:'6px 8px',fontSize:12,fontWeight:600,borderBottom:'1px solid #F8FAFC' }}>{size}</td>
                        <td style={{ padding:'6px 8px',fontSize:12,fontWeight:600,color:G,borderBottom:'1px solid #F8FAFC' }}>${Number(cost).toFixed(2)}</td>
                        <td style={{ padding:'6px 8px',fontSize:12,borderBottom:'1px solid #F8FAFC' }}>${(Number(cost)/(1-margin/100)).toFixed(2)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </>
              ) : sizePricingLoading ? (
                <div style={{ fontSize:12,color:'#94A3B8',marginBottom:14 }}>Loading your {p.supplier_name} pricing…</div>
              ) : (
                <div style={{ fontSize:12,color:'#94A3B8',marginBottom:14 }}>Live pricing unavailable right now — reopen to retry.</div>
              )
            ) : (
              <>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:8 }}>Quantity</div>
                <div style={{ display:'flex',gap:6,flexWrap:'wrap',marginBottom:14 }}>
                  {(p.pricing||[]).map((t,i)=>(
                    <button key={i} onClick={()=>setQty(t.qty||t.q)}
                      style={{ padding:'4px 12px',borderRadius:20,border:'1px solid',fontFamily:'inherit',cursor:'pointer',fontSize:12,
                        borderColor:qty===(t.qty||t.q)?G:'#E2E8F0',
                        background:qty===(t.qty||t.q)?`${G}18`:'#fff',
                        color:qty===(t.qty||t.q)?'#059669':'#64748B' }}>
                      {t.qty||t.q}+
                    </button>
                  ))}
                </div>
                <table style={{ width:'100%',borderCollapse:'collapse',marginBottom:14 }}>
                  <thead><tr>
                    {['Qty','List','Your Net','At 40% margin'].map(h=>(
                      <th key={h} style={{ fontSize:10,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',padding:'5px 8px',textAlign:'left',borderBottom:'1px solid #F1F5F9' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>{(p.pricing||[]).map((t,i)=>{
                    const Ds={A:.5,B:.45,C:.4,D:.35,E:.3,F:.25,G:.2,P:.5,Q:.45,R:.4,S:.35,T:.3,U:.25,V:.2,X:0};
                    const n2=+((t.list||t.l)*(1-(Ds[t.code||t.c]||0))).toFixed(2);
                    return <tr key={i} style={{ background:qty===(t.qty||t.q)?`${G}06`:'' }}>
                      <td style={{ padding:'6px 8px',fontSize:12,borderBottom:'1px solid #F8FAFC' }}>{t.qty||t.q}+</td>
                      <td style={{ padding:'6px 8px',fontSize:12,color:'#94A3B8',textDecoration:'line-through',borderBottom:'1px solid #F8FAFC' }}>${(t.list||t.l).toFixed(2)}</td>
                      <td style={{ padding:'6px 8px',fontSize:12,fontWeight:600,color:G,borderBottom:'1px solid #F8FAFC' }}>${n2.toFixed(2)}</td>
                      <td style={{ padding:'6px 8px',fontSize:12,borderBottom:'1px solid #F8FAFC' }}>${(+(n2/(1-0.4))).toFixed(2)}</td>
                    </tr>;
                  })}</tbody>
                </table>
              </>
            )}
            <div style={{ background:'#F8FAFC',borderRadius:9,padding:12,marginBottom:14 }}>
              <div style={{ fontSize:11,color:'#94A3B8',marginBottom:6 }}>Your margin calculator</div>
              <div style={{ display:'flex',alignItems:'center',gap:10 }}>
                <span style={{ fontSize:12,color:'#64748B' }}>Margin:</span>
                <input type="number" min="1" max="80" value={margin} onChange={e=>setMargin(+e.target.value)}
                  style={{ width:52,border:'1px solid #E2E8F0',borderRadius:6,padding:'3px 6px',fontSize:13,fontFamily:'inherit',textAlign:'center',outline:'none' }}/>
                <span style={{ fontSize:12,color:'#64748B' }}>%</span>
                <span style={{ marginLeft:'auto',fontSize:14,fontWeight:600,color:G }}>{hasSizePricing ? 'varies by size ↑' : `$${sellPrice}/unit`}</span>
              </div>
            </div>
            {p.decoration_methods?.length>0&&(
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11,color:'#94A3B8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6 }}>Decoration</div>
                <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                  {p.decoration_methods.map(d=><span key={d} style={{ background:'#F1F5F9',borderRadius:6,padding:'3px 9px',fontSize:11,color:'#475569' }}>{d}</span>)}
                </div>
              </div>
            )}
            <ShippingEstimator product={p} qty={qty} getToken={getToken}/>
          </div>
        </div>
        <div style={{ padding:'14px 24px',borderTop:'1px solid #E2E8F0',display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,padding:'9px 18px',fontSize:13,cursor:'pointer',fontFamily:'inherit',color:'#64748B' }}>Close</button>
          <button onClick={()=>{onAdd(p);onClose();}} style={{ background:G,border:'none',borderRadius:8,padding:'9px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:'inherit' }}>
            + Add to Presentation
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Product card (used in search grid) ──────────────────────────────────────
function ProductCard({ p, inCart, onClick, addToCart }) {
  const [imgError, setImgError] = useState(false);
  const netVal = fmtPrice(p);
  const imgUrl = cardImageUrl(p);
  const inventory = useMemo(() => getInventoryDisplay(p), [
    p.inventory_supports, p.inventory_status, p.inventory_quantity, p.inventory_checked_at,
  ]);
  return (
    <div onClick={onClick}
      style={{ background:'#fff',borderRadius:12,border:`1px solid ${inCart?G:'#E2E8F0'}`,overflow:'hidden',cursor:'pointer',transition:'all 0.2s' }}>
      <div style={{ height:150,background:'#F8FAFC',
        display:'flex',alignItems:'center',justifyContent:'center',fontSize:52,position:'relative',overflow:'hidden' }}>
        {imgUrl && !imgError ? (
          <img src={imgUrl} alt={p.name}
            onError={()=>setImgError(true)}
            style={{ maxWidth:'100%',maxHeight:'100%',objectFit:'contain' }}/>
        ) : (
          <span>{EMOJIS[p.category]||'📦'}</span>
        )}
        {inCart&&<span style={{ position:'absolute',top:8,left:8,background:G,color:'#fff',fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:10 }}>In Presentation</span>}
        {inventory && (
          <div style={{ position:'absolute',top:8,right:8 }}>
            <InventoryBadge inventory={inventory}/>
          </div>
        )}
      </div>
      <div style={{ padding:12 }}>
        <div style={{ fontSize:10,color:'#94A3B8',fontWeight:500,textTransform:'uppercase',letterSpacing:'0.5px' }}>
          <SupplierPopover product={p}>{p.supplier_name}</SupplierPopover>
          {p.supplier_sku && <span style={{ color:'#CBD5E1',fontWeight:400,textTransform:'none' }}> · #{p.supplier_sku}</span>}
        </div>
        <div style={{ fontSize:13,fontWeight:500,color:'#0F172A',margin:'3px 0 6px',lineHeight:1.3 }}>{p.name}</div>
        {!netVal&&p.pricing_pending&&<div style={{ fontSize:11,color:'#94A3B8' }}>Loading your pricing…</div>}
        {netVal&&<div style={{ display:'flex',alignItems:'baseline',gap:6 }}>
          <span style={{ fontSize:15,fontWeight:600,color:G }}>${netVal.toFixed(2)}</span>
          <span style={{ fontSize:11,color:'#94A3B8',textDecoration:'line-through' }}>${((p.pricing||[])[0]?.list||0).toFixed(2)}</span>
          <span style={{ fontSize:10,background:`${G}18`,color:'#059669',padding:'1px 6px',borderRadius:10,fontWeight:600 }}>net</span>
        </div>}
        <div style={{ fontSize:10,color:'#94A3B8',marginTop:3 }}>
          Min qty: {p.min_qty||'—'}
          {inventory && <span> · {inventory.freshness}</span>}
        </div>
      </div>
      <div style={{ padding:'9px 12px',borderTop:'1px solid #F8FAFC',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
        <span style={{ fontSize:11,color:'#94A3B8' }}>{p.category}</span>
        <button onClick={e=>{e.stopPropagation();addToCart(p);}}
          style={{ background:G,border:'none',borderRadius:7,color:'#fff',fontSize:11,fontWeight:500,padding:'5px 12px',cursor:'pointer',fontFamily:'inherit' }}>
          + Presentation
        </button>
      </div>
    </div>
  );
}

// Render up to 7 numbered page buttons with ellipsis when total exceeds that.
// Pattern: 1 … (cur-1) cur (cur+1) … last. For small page counts (≤7) just
// shows every page. Keeps the pager compact regardless of result-set size.
function renderPageButtons(current, total, setPage) {
  const btn = (n, isCurrent) => (
    <button
      key={n}
      type="button"
      onClick={() => setPage(n)}
      style={{
        minWidth:32,padding:'6px 10px',fontSize:13,borderRadius:6,
        border:`1px solid ${isCurrent ? G : '#CBD5E1'}`,
        background:isCurrent ? G : '#FFF',
        color:isCurrent ? '#FFF' : '#1E293B',
        fontWeight:isCurrent ? 600 : 400,
        cursor:isCurrent ? 'default' : 'pointer',
      }}
    >
      {n}
    </button>
  );
  const dots = (key) => (
    <span key={key} style={{ padding:'0 4px',color:'#94A3B8',fontSize:13 }}>…</span>
  );

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => btn(i + 1, i + 1 === current));
  }

  const out = [];
  out.push(btn(1, current === 1));
  if (current > 3) out.push(dots('dl'));

  // Window of 3 around current, clamped within [2, total-1]
  const winStart = Math.max(2, current - 1);
  const winEnd   = Math.min(total - 1, current + 1);
  for (let n = winStart; n <= winEnd; n++) out.push(btn(n, n === current));

  if (current < total - 2) out.push(dots('dr'));
  out.push(btn(total, current === total));
  return out;
}

// ─── Main search page ─────────────────────────────────────────────────────────
export default function SearchPage({ getToken, addToCart, cartItems, showToast }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg,     setMsg]     = useState('');
  const [cat,     setCat]     = useState('All');
  const [selected,setSelected]= useState(null);
  const [searched,setSearched]= useState(false);
  const [page,    setPage]    = useState(1);    // 1-indexed; resets on new results / cat change
  // Supplier + in-stock filters (added May 2026). Both are server-side filters;
  // changing either triggers a re-search via the effect below.
  const [supplier,        setSupplier]        = useState('');     // '' = all suppliers
  const [inStockOnly,     setInStockOnly]     = useState(false);
  // Min quantity filter (added June 2026). Empty string = no filter. When set
  // to a positive integer, hides products whose smallest pricing tier is above
  // this number. Critical for accurate search on a smaller catalog — without
  // this, "I need 25 tumblers" returns lots of 144+ qty products.
  const [minQuantity,     setMinQuantity]     = useState('');
  // Max price per unit filter (added June 2026). Empty string = no filter.
  // Filters by NET price (distributor cost) at the quantity tier matching
  // minQuantity if set, else the product's smallest tier.
  const [maxPrice,        setMaxPrice]        = useState('');
  const [suppliersList,   setSuppliersList]   = useState([]);     // [{name, product_count}, ...]
  const [supplierMenuOpen,setSupplierMenuOpen]= useState(false);
  const [supplierSearch,  setSupplierSearch]  = useState('');
  const supplierMenuRef = useRef(null);

  // Load suppliers list once — populates the dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const data = await api.withToken(token).suppliers();
        if (!cancelled) setSuppliersList(data.suppliers || []);
      } catch (e) {
        // Non-fatal — dropdown just stays empty. Toast would be noisy.
        console.error('Failed to load suppliers list:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // Click-outside closes the supplier dropdown.
  useEffect(() => {
    if (!supplierMenuOpen) return;
    function onDown(e) {
      if (supplierMenuRef.current && !supplierMenuRef.current.contains(e.target)) {
        setSupplierMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [supplierMenuOpen]);

  const doSearch = useCallback(async (q) => {
    setLoading(true); setMsg('');
    try {
      const token = await getToken();
      const mq = parseInt(minQuantity, 10);
      const mp = parseFloat(maxPrice);
      const data = await api.withToken(token).search(q, {
        category:    cat==='All' ? undefined : cat,
        supplier:    supplier || undefined,
        inStockOnly: inStockOnly || undefined,
        minQuantity: (Number.isFinite(mq) && mq > 0) ? mq : undefined,
        maxPrice:    (Number.isFinite(mp) && mp > 0) ? mp : undefined,
      });
      setResults(data.results||[]);
      setMsg(data.message||'');
      setSearched(true);
    } catch(e) { showToast('Search error: '+e.message,'error'); }
    finally { setLoading(false); }
  }, [getToken, cat, supplier, inStockOnly, minQuantity, maxPrice, showToast]);

  // Auto re-search when supplier or in-stock filter changes (only after the
  // user has searched at least once — otherwise we'd fire a search on initial
  // mount). We use a ref to invoke the latest doSearch with the latest query,
  // so the effect's dependency array can include only the filter values that
  // should actually trigger a re-search. This avoids ESLint exhaustive-deps
  // warnings without needing a disable directive (CRA's config treats unknown
  // rule names as build errors — see earlier deploy failure).
  const latestSearchRef = useRef(null);
  latestSearchRef.current = () => doSearch(query);
  useEffect(() => {
    if (searched && latestSearchRef.current) latestSearchRef.current();
  }, [supplier, inStockOnly, minQuantity, maxPrice, searched]);

  const displayed = cat==='All' ? results : results.filter(p=>p.category===cat);

  // Reset to page 1 whenever the underlying result set changes — new search,
  // new filter, or a category change that shrinks the list past the current
  // page. Depending on `displayed.length` covers all three without needing
  // separate hooks. (Server-side filters supplier/inStockOnly trigger a new
  // search via the effect above, which replaces `results`, which trips this.)
  useEffect(() => { setPage(1); }, [results, cat]);

  const pageCount = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  // Clamp page when displayed shrinks below the current slice — avoids the
  // grid going briefly empty before the reset effect runs.
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd   = Math.min(pageStart + PAGE_SIZE, displayed.length);
  const pageItems = displayed.slice(pageStart, pageEnd);

  // Supplier dropdown filtering — type-ahead over the loaded list.
  const filteredSuppliers = useMemo(() => {
    const q = supplierSearch.trim().toLowerCase();
    if (!q) return suppliersList;
    return suppliersList.filter(s => s.name.toLowerCase().includes(q));
  }, [suppliersList, supplierSearch]);

  return (
    <div style={{ padding:24 }}>
      <div style={{ background:`linear-gradient(135deg,${BRAND.colors.dark},#1E293B)`,borderRadius:16,padding:28,marginBottom:14,position:'relative',overflow:'visible' }}>
        {/* Decorative circle in its own clipped layer so the parent box can
            overflow (needed for the supplier dropdown to escape the box).
            Without this nested layer the circle would visibly spill past
            the hero's rounded corners. */}
        <div style={{ position:'absolute',inset:0,borderRadius:16,overflow:'hidden',pointerEvents:'none' }}>
          <div style={{ position:'absolute',top:-50,right:-50,width:200,height:200,background:'rgba(16,185,129,0.08)',borderRadius:'50%' }}/>
        </div>
        <div style={{ fontSize:20,fontWeight:600,color:'#fff',marginBottom:4,letterSpacing:'-0.3px',position:'relative' }}>Search Products</div>
        <div style={{ fontSize:13,color:'#94A3B8',marginBottom:18,position:'relative' }}>AI-powered</div>

        {/* Search bar */}
        <div style={{ display:'flex',gap:8,marginBottom:14 }}>
          <input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doSearch(query)}
            placeholder="Try: eco bags under $8 for healthcare, or tumblers for 250 people..."
            style={{ flex:1,background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.12)',
              borderRadius:10,padding:'11px 16px',color:'#fff',fontSize:14,fontFamily:'inherit',outline:'none' }}/>
          <button onClick={()=>doSearch(query)} disabled={loading}
            style={{ background:G,border:'none',borderRadius:10,padding:'11px 20px',color:'#fff',fontSize:13,fontWeight:500,cursor:loading?'wait':'pointer',fontFamily:'inherit',opacity:loading?0.7:1 }}>
            {loading?'Searching...':'Search'}
          </button>
        </div>

        {/* Category chips — inside the hero. Dark-bg-styled to fit. */}
        <div style={{ display:'flex',flexWrap:'wrap',gap:6,marginBottom:12 }}>
          {CATS.map(c=>(
            <button key={c} onClick={()=>setCat(c)}
              style={{
                background: cat===c ? G : 'rgba(255,255,255,0.06)',
                border: cat===c ? `1px solid ${G}` : '1px solid rgba(255,255,255,0.12)',
                borderRadius:20, padding:'4px 12px', fontSize:11,
                color: cat===c ? '#fff' : '#CBD5E1',
                cursor:'pointer', fontFamily:'inherit',
              }}>
              {c}
            </button>
          ))}
        </div>

        {/* Filter row — supplier dropdown + in-stock checkbox, inside hero */}
        <div style={{ display:'flex',gap:10,alignItems:'center',flexWrap:'wrap' }}>
          <div ref={supplierMenuRef} style={{ position:'relative' }}>
            <button onClick={()=>setSupplierMenuOpen(o=>!o)}
              style={{
                background: supplier ? `${G}22` : 'rgba(255,255,255,0.06)',
                border: `1px solid ${supplier ? G : 'rgba(255,255,255,0.12)'}`,
                borderRadius:8, padding:'6px 12px', fontSize:12,
                color: supplier ? '#34D399' : '#CBD5E1',
                cursor:'pointer', fontFamily:'inherit',
                display:'flex', alignItems:'center', gap:8, minWidth:200,
              }}>
              <span style={{ flex:1,textAlign:'left',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
                {supplier || 'All suppliers'}
              </span>
              {supplier && (
                <span onClick={e=>{e.stopPropagation();setSupplier('');setSupplierSearch('');}}
                  title="Clear supplier filter"
                  style={{ color:'#94A3B8',cursor:'pointer',padding:'0 2px',fontSize:14,lineHeight:1 }}>×</span>
              )}
              <span style={{ color:'#94A3B8',fontSize:10 }}>▾</span>
            </button>

            {supplierMenuOpen && (
              <div style={{ position:'absolute',top:'100%',left:0,marginTop:4,zIndex:50,
                background:'#fff',border:'1px solid #E2E8F0',borderRadius:8,
                boxShadow:'0 8px 24px rgba(15,23,42,0.28)',width:340,maxHeight:360,overflow:'hidden',
                display:'flex',flexDirection:'column' }}>
                <input autoFocus type="text" placeholder="Search suppliers..."
                  value={supplierSearch} onChange={e=>setSupplierSearch(e.target.value)}
                  style={{ padding:'8px 12px',fontSize:12,border:'none',borderBottom:'1px solid #E2E8F0',
                    fontFamily:'inherit',outline:'none' }}/>
                <div style={{ overflowY:'auto' }}>
                  <div onClick={()=>{setSupplier('');setSupplierSearch('');setSupplierMenuOpen(false);}}
                    style={{ padding:'8px 12px',fontSize:12,cursor:'pointer',color:supplier===''?G:'#0F172A',
                      background:supplier===''?`${G}0D`:'transparent',fontWeight:supplier===''?600:400 }}
                    onMouseEnter={e=>{if (supplier !== '') e.currentTarget.style.background='#F8FAFC';}}
                    onMouseLeave={e=>{if (supplier !== '') e.currentTarget.style.background='transparent';}}>
                    All suppliers
                  </div>
                  {filteredSuppliers.length === 0 && (
                    <div style={{ padding:'12px',fontSize:11,color:'#94A3B8',textAlign:'center' }}>
                      No matches
                    </div>
                  )}
                  {filteredSuppliers.map(s => (
                    <div key={s.name}
                      onClick={()=>{setSupplier(s.name);setSupplierSearch('');setSupplierMenuOpen(false);}}
                      style={{ padding:'8px 12px',fontSize:12,cursor:'pointer',
                        display:'flex',justifyContent:'space-between',alignItems:'center',gap:8,
                        color:supplier===s.name?G:'#0F172A',
                        background:supplier===s.name?`${G}0D`:'transparent',
                        fontWeight:supplier===s.name?600:400 }}
                      onMouseEnter={e=>{if (supplier !== s.name) e.currentTarget.style.background='#F8FAFC';}}
                      onMouseLeave={e=>{if (supplier !== s.name) e.currentTarget.style.background='transparent';}}>
                      <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{s.name}</span>
                      <span style={{ color:'#94A3B8',fontSize:10,fontWeight:400,flexShrink:0 }}>{s.product_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <label style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:'#CBD5E1',cursor:'pointer' }}>
            <input type="checkbox" checked={inStockOnly} onChange={e=>setInStockOnly(e.target.checked)}
              style={{ cursor:'pointer' }}/>
            In stock only
          </label>

          {/* Min Qty filter — June 2026. Hides products whose smallest pricing
              tier exceeds this quantity. Critical because some suppliers only
              sell in lots of 144+ and distributors searching for small orders
              don't want to see them. Empty input = no filter. */}
          <label style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:'#CBD5E1' }}>
            <span>Min qty</span>
            <input
              type="number" min="1" placeholder="any"
              value={minQuantity}
              onChange={e => {
                // Only allow positive integers in the field. Empty string = no filter.
                const v = e.target.value;
                if (v === '' || /^\d+$/.test(v)) setMinQuantity(v);
              }}
              style={{
                width: 70, padding:'4px 8px', fontSize:12,
                background:'#0F172A', border:'1px solid #1E293B', borderRadius:6,
                color:'#fff', outline:'none',
              }}
            />
          </label>

          {/* Max price-per-unit filter — June 2026. Filters by NET price
              (distributor cost). Tier used = matches Min qty if set, else
              the product's smallest tier. */}
          <label style={{ display:'inline-flex',alignItems:'center',gap:6,fontSize:12,color:'#CBD5E1' }}>
            <span>Max&nbsp;$</span>
            <input
              type="number" min="0" step="0.01" placeholder="any"
              value={maxPrice}
              onChange={e => {
                // Accept positive numbers (decimals OK). Empty = no filter.
                const v = e.target.value;
                if (v === '' || /^\d+(\.\d{0,2})?$/.test(v)) setMaxPrice(v);
              }}
              style={{
                width: 70, padding:'4px 8px', fontSize:12,
                background:'#0F172A', border:'1px solid #1E293B', borderRadius:6,
                color:'#fff', outline:'none',
              }}
            />
          </label>
        </div>
      </div>

      {/* AI-prompt suggestion chips removed June 2026 — they implied
          natural-language curation that the keyword-based catalog search
          doesn't deliver, and set wrong expectations vs. the AI Bundle
          Builder (which IS the right tool for "tumblers for 250 corporate
          gift recipients" style queries). */}

      {msg && <div style={{ fontSize:12,color:'#059669',background:`${G}0D`,border:`1px solid ${G}22`,borderRadius:8,padding:'8px 14px',marginBottom:14,display:'flex',alignItems:'center',gap:6 }}>
        ✓ {msg}
      </div>}

      {!searched && !loading && (
        <div style={{ textAlign:'center',padding:'48px 24px',color:'#94A3B8' }}>
          <div style={{ fontSize:40,marginBottom:12 }}>🔍</div>
          <div style={{ fontSize:15,fontWeight:500,color:'#374151',marginBottom:6 }}>Search products</div>
          <div style={{ fontSize:13 }}>Type a query above or click an example</div>
        </div>
      )}

      <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))',gap:14 }}>
        {pageItems.map(p => (
          <ProductCard key={p.id} p={p}
            inCart={cartItems.some(x=>baseProductId(x)===p.id)}
            onClick={()=>setSelected(p)}
            addToCart={addToCart}/>
        ))}
      </div>

      {/* Pagination — only render when there's more than one page. Page size
          (24) is module-scoped above; "Showing X–Y of Z" reflects the current
          slice of `displayed`, which is already category-filtered. */}
      {displayed.length > PAGE_SIZE && (
        <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12,marginTop:20,paddingTop:16,borderTop:'1px solid #E2E8F0' }}>
          <div style={{ fontSize:13,color:'#64748B' }}>
            Showing <strong style={{ color:'#1E293B' }}>{pageStart + 1}–{pageEnd}</strong> of <strong style={{ color:'#1E293B' }}>{displayed.length}</strong>
          </div>
          <div style={{ display:'flex',alignItems:'center',gap:6 }}>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              style={{
                padding:'6px 12px',fontSize:13,borderRadius:6,border:'1px solid #CBD5E1',
                background:safePage <= 1 ? '#F1F5F9' : '#FFF',
                color:safePage <= 1 ? '#94A3B8' : '#1E293B',
                cursor:safePage <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              ‹ Prev
            </button>
            {renderPageButtons(safePage, pageCount, setPage)}
            <button
              type="button"
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
              disabled={safePage >= pageCount}
              style={{
                padding:'6px 12px',fontSize:13,borderRadius:6,border:'1px solid #CBD5E1',
                background:safePage >= pageCount ? '#F1F5F9' : '#FFF',
                color:safePage >= pageCount ? '#94A3B8' : '#1E293B',
                cursor:safePage >= pageCount ? 'not-allowed' : 'pointer',
              }}
            >
              Next ›
            </button>
          </div>
        </div>
      )}

      {selected && <ProductModal product={selected} getToken={getToken} onClose={()=>setSelected(null)} onAdd={addToCart}
        onProductRefreshed={(u)=>{
          setSelected(u);
          setResults(rs => rs.map(r => r.id === u.id ? { ...r, ...u } : r));
        }}/>}
    </div>
  );
}
